import React from 'react';
import s from '../DietPlanBuilder.module.css';
import type { MacroKey, OptionStatus, Pct } from '../../types';

export const MACRO_COLOR: Record<MacroKey, string> = { p: 'var(--gf-protein)', c: 'var(--gf-carbs)', f: 'var(--gf-fat)' };

/* Icons (stroke) */
const I = ({ d, size = 18, sw = 2.2 }: { d: string; size?: number; sw?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d={d} /></svg>
);
export const Icon = {
  Back: (p: { size?: number }) => <I d="M15 18l-6-6 6-6" {...p} />,
  Chevron: (p: { size?: number }) => <I d="M9 6l6 6-6 6" {...p} />,
  Down: (p: { size?: number }) => <I d="M6 9l6 6 6-6" {...p} />,
  Up: (p: { size?: number }) => <I d="M6 15l6-6 6 6" {...p} />,
  Plus: (p: { size?: number }) => <I d="M12 5v14M5 12h14" {...p} sw={2.4} />,
  Minus: (p: { size?: number }) => <I d="M5 12h14" {...p} sw={2.4} />,
  X: (p: { size?: number }) => <I d="M18 6 6 18M6 6l12 12" {...p} />,
  Pen: (p: { size?: number }) => <svg width={p.size ?? 14} height={p.size ?? 14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></svg>,
  Search: (p: { size?: number }) => <svg width={p.size ?? 18} height={p.size ?? 18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>,
  Spark: (p: { size?: number }) => <svg width={p.size ?? 15} height={p.size ?? 15} viewBox="0 0 24 24" fill="currentColor" aria-hidden><path d="M12 2l2.2 6.6L21 11l-6.8 2.4L12 20l-2.2-6.6L3 11l6.8-2.4z" /></svg>,
  Lines: (p: { size?: number }) => <I d="M4 6h16M4 12h10M4 18h7" {...p} sw={1.9} />,
};

export const Pill = ({ tone, children }: { tone: 'green' | 'amber' | 'red' | 'grey' | 'blue'; children: React.ReactNode }) => (
  <span className={{ green: s.pillGreen, amber: s.pillAmber, red: s.pillRed, grey: s.pillGrey, blue: s.pillBlue }[tone]}>{children}</span>
);
export const toneFor = (st: OptionStatus): 'green' | 'amber' | 'red' | 'grey' => (st === 'ok' ? 'green' : st === 'warn' ? 'amber' : st === 'bad' ? 'red' : 'grey');
export const colorFor = (st: OptionStatus) => ({ ok: 'var(--gf-green-ink)', warn: 'var(--gf-amber-ink)', bad: 'var(--gf-red-ink)', empty: 'var(--gf-faint)' }[st]);

export const MacroBar = ({ pct, size }: { pct: Pct; size?: 's' }) => (
  <div className={s.macroBar} data-size={size} aria-hidden>
    {(['p', 'c', 'f'] as MacroKey[]).map(k => <div key={k} style={{ width: `${pct[k]}%`, background: MACRO_COLOR[k] }} />)}
  </div>
);

export const Stepper = ({ value, unit, onDec, onInc, size = 'lg' }: { value: React.ReactNode; unit?: string; onDec: () => void; onInc: () => void; size?: 'lg' | 'sm' }) => (
  <div className={s.stepper}>
    <button type="button" className={size === 'lg' ? s.stepBtn : s.stepBtnSm} onClick={onDec} aria-label="Decrease"><Icon.Minus size={size === 'lg' ? 20 : 16} /></button>
    <div className={s.stepVal}><b>{value}</b>{unit && <span className={s.muted} style={{ marginLeft: 4, fontWeight: 600 }}>{unit}</span>}</div>
    <button type="button" className={size === 'lg' ? s.stepBtn : s.stepBtnSm} onClick={onInc} aria-label="Increase"><Icon.Plus size={size === 'lg' ? 20 : 16} /></button>
  </div>
);

export const Toggle = ({ on, onChange, label }: { on: boolean; onChange: (v: boolean) => void; label: string }) => (
  <button type="button" role="switch" aria-checked={on} aria-label={label} className={s.toggle} data-on={on} onClick={() => onChange(!on)} />
);

export const CheckRow = ({ on, onChange, children }: { on: boolean; onChange: (v: boolean) => void; children: React.ReactNode }) => (
  <button type="button" role="checkbox" aria-checked={on} className={s.checkRow} onClick={() => onChange(!on)}>
    <span className={s.checkBox} data-on={on}>{on ? '✓' : ''}</span>
    <span className={s.grow}>{children}</span>
  </button>
);

export const Chip = ({ on, onClick, children }: { on: boolean; onClick: () => void; children: React.ReactNode }) => (
  <button type="button" className={s.chip} data-on={on} aria-pressed={on} onClick={onClick}>{children}</button>
);

export const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <label className={s.col}><span className={s.eyebrow}>{label}</span>{children}</label>
);

/** Bottom sheet on mobile, centered dialog on web — same children. */
export function Sheet({ title, sub, onClose, onBack, children, footer }: { title: string; sub?: string; onClose: () => void; onBack?: () => void; children: React.ReactNode; footer?: React.ReactNode }) {
  React.useEffect(() => { const h = (e: KeyboardEvent) => e.key === 'Escape' && onClose(); window.addEventListener('keydown', h); return () => window.removeEventListener('keydown', h); }, [onClose]);
  return (
    <div className={s.scrim} role="dialog" aria-modal aria-label={title}>
      <div className={s.scrimBg} onClick={onClose} />
      <div className={s.sheet}>
        <div className={s.sheetHandle} />
        <div className={s.sheetHead}>
          {onBack && <button type="button" className={s.iconBtnGhost} onClick={onBack} aria-label="Back" style={{ marginLeft: -8 }}><Icon.Back /></button>}
          <div className={s.grow}><div className={s.title}>{title}</div>{sub && <div className={s.sub}>{sub}</div>}</div>
          <button type="button" className={s.iconBtn} onClick={onClose} aria-label="Close" style={{ width: 34, height: 34 }}><Icon.X size={16} /></button>
        </div>
        {children}
        {footer && <div className={s.sheetFoot}>{footer}</div>}
      </div>
    </div>
  );
}
